import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import PartyEntryDialog from "../components/PartyEntryDialog";
import api, { apiErrorText } from "../lib/api";
import { currentNepalMonth, fmtUSDT, monthAdd, monthLabel } from "../lib/partyBook";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  ArrowDownLeft,
  ArrowUpRight,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Eye,
  Plus,
  Scale,
  Users,
} from "lucide-react";
import { toast } from "sonner";

const EMPTY_PARTY = { name: "", contact: "", notes: "" };

export default function PartyBook() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState("all");
  const [month, setMonth] = useState(currentNepalMonth());
  const [dashboard, setDashboard] = useState({ summary: {}, parties: [] });
  const [loading, setLoading] = useState(true);
  const [partyOpen, setPartyOpen] = useState(false);
  const [partyForm, setPartyForm] = useState(EMPTY_PARTY);
  const [savingParty, setSavingParty] = useState(false);
  const [entryOpen, setEntryOpen] = useState(false);
  const [entryPartyId, setEntryPartyId] = useState("");

  const filterParams = useMemo(() => {
    const params = { period };
    if (period === "monthly") params.month = month;
    return params;
  }, [period, month]);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const response = await api.get("/party-book/dashboard", { params: filterParams });
      setDashboard(response.data);
      return response.data;
    } catch (error) {
      toast.error(apiErrorText(error));
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterParams]);

  const saveParty = async () => {
    if (!partyForm.name.trim()) return toast.error("Party name is required");
    setSavingParty(true);
    try {
      await api.post("/party-book/parties", {
        name: partyForm.name.trim(),
        contact: partyForm.contact.trim() || null,
        notes: partyForm.notes.trim() || null,
      });
      toast.success("Party added");
      setPartyOpen(false);
      setPartyForm(EMPTY_PARTY);
      await loadDashboard();
    } catch (error) {
      toast.error(apiErrorText(error));
    } finally {
      setSavingParty(false);
    }
  };

  const openEntry = (party = null) => {
    const partyId = party?.id || dashboard.parties?.[0]?.id || "";
    if (!partyId) return toast.message("Add a party first");
    setEntryPartyId(partyId);
    setEntryOpen(true);
  };

  const summary = dashboard.summary || {};
  const parties = dashboard.parties || [];
  const filterLabel = period === "all" ? "All Time" : monthLabel(month);

  return (
    <Layout
      title="Party Book"
      subtitle="Party-wise USDT sent, received, and net ledger · Nepal time"
      testId="party-book-page"
      actions={
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => openEntry()} disabled={!parties.length} className="h-9">
            <Plus size={14} className="mr-1.5" /> Add entry
          </Button>
          <Button onClick={() => setPartyOpen(true)} className="h-9 bg-zinc-900 text-white hover:bg-zinc-800">
            <Plus size={14} className="mr-1.5" /> Add party
          </Button>
        </div>
      }
    >
      <div className="stat-card mb-5 flex flex-wrap items-center gap-2 p-3">
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="h-8 w-[150px]" data-testid="party-period-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Time</SelectItem>
            <SelectItem value="monthly">Monthly</SelectItem>
          </SelectContent>
        </Select>
        {period === "monthly" && (
          <>
            <button onClick={() => setMonth(monthAdd(month, -1))} className="flex h-8 w-8 items-center justify-center rounded-md border hairline bg-white hover:bg-zinc-50" aria-label="Previous month">
              <ChevronLeft size={14} strokeWidth={1.7} />
            </button>
            <div className="min-w-[130px] px-2 text-center font-display text-[14px] font-semibold">{monthLabel(month)}</div>
            <button onClick={() => setMonth(monthAdd(month, 1))} className="flex h-8 w-8 items-center justify-center rounded-md border hairline bg-white hover:bg-zinc-50" aria-label="Next month">
              <ChevronRight size={14} strokeWidth={1.7} />
            </button>
            {month !== currentNepalMonth() && (
              <button onClick={() => setMonth(currentNepalMonth())} className="h-8 rounded-md border hairline bg-white px-3 text-[12px] hover:bg-zinc-50">This month</button>
            )}
          </>
        )}
        <div className="ml-auto flex items-center gap-1.5 text-[11.5px] text-zinc-500">
          <CalendarDays size={13} /> Asia/Kathmandu
        </div>
      </div>

      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <SummaryCard label="Total Sent" value={summary.total_sent} icon={ArrowUpRight} tone="rose" footer={`${summary.entry_count || 0} total entries · ${filterLabel}`} testId="party-total-sent" />
        <SummaryCard label="Total Received" value={summary.total_received} icon={ArrowDownLeft} tone="emerald" footer={`${summary.party_count || 0} parties · ${filterLabel}`} testId="party-total-received" />
        <SummaryCard label="Net" value={summary.net_usdt} icon={Scale} tone="sky" footer={`Received − Sent · ${filterLabel}`} testId="party-net-balance" />
      </div>

      <section className="stat-card overflow-hidden" data-testid="party-list-section">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b hairline px-5 py-4">
          <div>
            <h2 className="font-display text-[16px] font-semibold text-zinc-900">Parties</h2>
            <p className="mt-0.5 text-[12px] text-zinc-500">Party-wise totals for {filterLabel.toLowerCase()}</p>
          </div>
          <Button variant="outline" onClick={() => setPartyOpen(true)} className="h-8">
            <Plus size={13} className="mr-1.5" /> Add party
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-[13px]">
            <thead>
              <tr className="border-b hairline bg-zinc-50/50 text-left text-[10.5px] uppercase tracking-widest text-zinc-500">
                <th className="px-4 py-3 font-medium">Party</th>
                <th className="px-4 py-3 text-right font-medium">Received</th>
                <th className="px-4 py-3 text-right font-medium">Sent</th>
                <th className="px-4 py-3 text-right font-medium">Net</th>
                <th className="px-4 py-3 text-right font-medium">Entries</th>
                <th className="px-4 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y hairline">
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-zinc-500">Loading...</td></tr>
              ) : parties.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-14 text-center text-zinc-500">
                    <Users size={22} strokeWidth={1.5} className="mx-auto mb-2 text-zinc-400" />
                    No parties yet.
                    <button onClick={() => setPartyOpen(true)} className="ml-1 font-medium text-zinc-900 underline underline-offset-2">Add the first party</button>
                  </td>
                </tr>
              ) : parties.map((party) => (
                <tr key={party.id} className="row-hover">
                  <td className="px-4 py-3">
                    <button onClick={() => navigate(`/party-book/${party.id}`)} className="text-left">
                      <div className="font-medium text-zinc-900 hover:underline">{party.name}</div>
                      <div className="text-[11px] text-zinc-500">{party.contact || "No contact"}</div>
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-emerald-700">{fmtUSDT(party.total_received)}</td>
                  <td className="px-4 py-3 text-right font-mono text-rose-700">{fmtUSDT(party.total_sent)}</td>
                  <td className={`px-4 py-3 text-right font-mono ${Number(party.net_usdt) < 0 ? "text-rose-700" : "text-zinc-900"}`}>{fmtUSDT(party.net_usdt)}</td>
                  <td className="px-4 py-3 text-right font-mono">{party.entry_count || 0}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => navigate(`/party-book/${party.id}`)} className="rounded p-1.5 text-zinc-600 hover:bg-zinc-100" title="View full ledger"><Eye size={14} strokeWidth={1.6} /></button>
                      <button onClick={() => openEntry(party)} className="rounded p-1.5 text-zinc-600 hover:bg-zinc-100" title="Add entry"><Plus size={14} strokeWidth={1.6} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <Dialog open={partyOpen} onOpenChange={setPartyOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">Add party</DialogTitle>
            <DialogDescription>Create a party before recording sent or received entries.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Party name *</Label>
              <Input value={partyForm.name} onChange={(event) => setPartyForm({ ...partyForm, name: event.target.value })} placeholder="Party or company name" />
            </div>
            <div className="space-y-1.5">
              <Label>Contact</Label>
              <Input value={partyForm.contact} onChange={(event) => setPartyForm({ ...partyForm, contact: event.target.value })} placeholder="Phone, email, or contact person" />
            </div>
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Input value={partyForm.notes} onChange={(event) => setPartyForm({ ...partyForm, notes: event.target.value })} placeholder="Optional" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPartyOpen(false)}>Cancel</Button>
            <Button onClick={saveParty} disabled={savingParty} className="bg-zinc-900 text-white hover:bg-zinc-800">{savingParty ? "Adding..." : "Add party"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PartyEntryDialog
        open={entryOpen}
        onOpenChange={setEntryOpen}
        parties={parties}
        initialPartyId={entryPartyId}
        onSaved={loadDashboard}
      />
    </Layout>
  );
}

function SummaryCard({ label, value, icon: Icon, tone, footer, testId }) {
  const tones = {
    rose: { icon: "bg-rose-50 text-rose-600", value: "text-rose-700" },
    emerald: { icon: "bg-emerald-50 text-emerald-600", value: "text-emerald-700" },
    sky: { icon: "bg-sky-50 text-sky-600", value: "text-zinc-900" },
  };
  const selected = tones[tone] || tones.sky;
  return (
    <div className="stat-card p-5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="text-[10.5px] uppercase tracking-widest text-zinc-500">{label}</span>
        <span className={`flex h-8 w-8 items-center justify-center rounded-md ${selected.icon}`}><Icon size={16} strokeWidth={1.7} /></span>
      </div>
      <div className={`mt-3 font-mono text-[23px] font-medium ${selected.value}`}>{fmtUSDT(value)}</div>
      <div className="mt-1.5 text-[10.5px] text-zinc-400">{footer}</div>
    </div>
  );
}
